import { useEffect, useState } from 'react';
import {
  Dialog,
  InputText,
  InputType,
  RadioGroupControl,
  TextV2,
} from '@insights-ltd/design-library/components';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { NUMERICAL_ONLY_REGEX } from 'variables';

export type TransferUnitsDialogProps = {
  open: boolean;
  profileCost: number;
  availableUnits: number;
  recoverableUnits: number;
  doTransferUnits: (amount: number, isAdding: boolean) => void;
  onClose: () => void;
};

type TransferUnitsData = {
  amount: number;
};

const AlertWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  background: theme.palette.primary.light,
  borderRadius: '4px',
}));

const DetailGridP = styled(TextV2)({
  textAlign: 'right',
  overflowWrap: 'anywhere',
});

const UnitDetails = ({
  title,
  value,
  testId,
  margin = 0,
}: {
  title: string;
  value: string;
  testId: string;
  margin?: number;
}) => {
  return (
    <Grid container sx={{ marginTop: margin, marginBottom: margin }}>
      <Grid size={9}>
        <TextV2 variant="bodyBold" color="textSecondary">
          {title}
        </TextV2>
      </Grid>
      <Grid size={3}>
        <DetailGridP variant="bodyBold" data-testid={testId}>
          {value}
        </DetailGridP>
      </Grid>
    </Grid>
  );
};

const TransferUnitsDialog = ({
  open,
  onClose,
  profileCost,
  availableUnits,
  recoverableUnits,
  doTransferUnits,
}: TransferUnitsDialogProps) => {
  const { t } = useTranslation();
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
    reset,
    setFocus,
  } = useForm<TransferUnitsData>({
    defaultValues: {
      amount: 1,
    },
  });
  const [transactionType, setTransactionType] = useState<string>('ADD_UNITS');
  const isAdding = transactionType === 'ADD_UNITS';
  const [amount] = watch(['amount']);

  useEffect(() => {
    setFocus('amount');
  }, [transactionType, setFocus]);

  const transferUnits = async () => {
    doTransferUnits(amount, isAdding);
    onClose();
    reset();
    setTransactionType('ADD_UNITS');
  };

  const handleClose = () => {
    onClose();
    reset();
    setTransactionType('ADD_UNITS');
  };

  const errorMessages: Record<string, string> = {
    required: isAdding
      ? t('ui.event-management.events.transfer-units-dialog.add-validation.min')
      : t(
          'ui.event-management.events.transfer-units-dialog.recover-validation.min',
        ),
    pattern: t(
      'ui.event-management.events.transfer-units-dialog.validation.non-numeric',
    ),
    min: isAdding
      ? t('ui.event-management.events.transfer-units-dialog.add-validation.min')
      : t(
          'ui.event-management.events.transfer-units-dialog.recover-validation.min',
        ),
    max: isAdding
      ? t('ui.event-management.events.transfer-units-dialog.add-validation.max')
      : t(
          'ui.event-management.events.transfer-units-dialog.recover-validation.max',
        ),
  };
  const helperText = errors.amount
    ? errorMessages[errors.amount.type]
    : undefined;
  const unitCost = `${profileCost} ${t(
    'ui.event-management.events.unit-management.value.units',
  )}`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit(transferUnits)}
      title={t('ui.event-management.events.transfer-units-dialog.title')}
      dialogProps={{
        slotProps: { transition: { onEntered: () => setFocus('amount') } },
      }}
      content={
        <>
          {recoverableUnits > 0 && (
            <>
              <TextV2>
                {t('ui.event-management.events.transfer-units-dialog.options')}
              </TextV2>
              <RadioGroupControl
                options={[
                  {
                    value: 'ADD_UNITS',
                    label: t(
                      'ui.event-management.events.transfer-units-dialog.add',
                    ),
                  },
                  {
                    value: 'RECOVER_UNITS',
                    label: t(
                      'ui.event-management.events.transfer-units-dialog.recover',
                    ),
                  },
                ]}
                name="transationType"
                title={t(
                  'ui.event-management.events.transfer-units-dialog.options',
                )}
                value={transactionType}
                onChange={setTransactionType}
              />
            </>
          )}
          <Box sx={{ height: spacingSizeMap.L }} />
          <TextV2 variant="body1">
            {isAdding
              ? t('ui.event-management.events.transfer-units-dialog.body')
              : t(
                  'ui.event-management.events.transfer-units-dialog.body.recover',
                )}
          </TextV2>
          <UnitDetails
            testId="profile-cost"
            title={
              isAdding
                ? t('ui.event-management.events.transfer-units-dialog.cost')
                : t(
                    'ui.event-management.events.transfer-units-dialog.recoverable',
                  )
            }
            value={isAdding ? unitCost : String(recoverableUnits)}
            margin={spacingSizeMap.S}
          />
          <InputText
            id="request-units-amount"
            required
            type={InputType.NUMBER}
            fullWidth
            {...register('amount', {
              required: true,
              min: 1,
              max: isAdding ? availableUnits : recoverableUnits,
              pattern: NUMERICAL_ONLY_REGEX, // regex to only allow numerical inputs
            })}
            min={1}
            max={isAdding ? availableUnits : recoverableUnits}
            label={
              isAdding
                ? t(
                    'ui.event-management.events.transfer-units-dialog.units.label',
                  )
                : t(
                    'ui.event-management.events.transfer-units-dialog.units.label.recover',
                  )
            }
            error={Boolean(errors.amount)}
            helperText={helperText}
          />
          <Box sx={{ marginBottom: spacingSizeMap.S }} />
          <AlertWrapper>
            <UnitDetails
              testId="current-balance"
              title={t(
                'ui.event-management.events.transfer-units-dialog.balance',
              )}
              value={String(availableUnits)}
            />
            <UnitDetails
              testId="final-balance"
              title={t(
                'ui.event-management.events.transfer-units-dialog.after',
              )}
              value={
                isAdding
                  ? String(availableUnits - Number(amount))
                  : String(availableUnits + Number(amount))
              }
            />
          </AlertWrapper>
        </>
      }
      primaryButtonText={
        isAdding
          ? t('ui.event-management.events.transfer-units-dialog.confirm')
          : t(
              'ui.event-management.events.transfer-units-dialog.confirm.recover',
            )
      }
      secondaryButtonText={t(
        'ui.event-management.events.transfer-units-dialog.cancel',
      )}
    />
  );
};

export default TransferUnitsDialog;
